import React, { useState, useRef, useCallback, useEffect } from "react";
import { LayerGroupName, LayerName } from "types/dataModel";
import "./mobile_flood_selector.css";
import { useIsTouchDevice } from "hooks/useIsTouchDevice";

interface MobileFloodSelectorProps {
  layersToggle: Record<string, boolean>;
  toggleLayer: (layer: string) => void;
  layerGroup: string;
  onSlideChange?: (slidePercent: number) => void;
}

export const MobileFloodSelector: React.FC<MobileFloodSelectorProps> = ({
  layersToggle,
  toggleLayer,
  layerGroup,
  onSlideChange,
}) => {
  const isTouchDevice = useIsTouchDevice();

  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [currentSlidePercent, setCurrentSlidePercent] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    setDragStartX(e.touches[0].clientX);
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;

      const currentX = e.touches[0].clientX;
      const deltaX = currentX - dragStartX;

      // Calculate the new slide percentage based on the current touch position
      const newSlidePercent = Math.max(
        0,
        Math.min(100, (currentX / window.innerWidth) * 100),
      );

      setCurrentSlidePercent(newSlidePercent);

      // Update the visual offset for smooth movement
      setDragOffset(deltaX);

      if (onSlideChange) {
        onSlideChange(newSlidePercent);
      }
    },
    [isDragging, dragStartX, onSlideChange],
  );

  const handleTouchEnd = useCallback(() => {
    const threshold = 50;

    if (Math.abs(dragOffset) > threshold) {
      if (dragOffset > 0) {
        if (!layersToggle[LayerName.FLOODING_NOMANG]) {
          toggleLayer(LayerName.FLOODING_NOMANG);
        }
      } else {
        if (!layersToggle[LayerName.FLOODING_LATEST]) {
          toggleLayer(LayerName.FLOODING_LATEST);
        }
      }
    }

    setIsDragging(false);
  }, [dragOffset, layersToggle, toggleLayer]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handle = container.querySelector(".mobile-flood-handle");
    if (!handle) return;

    const handleTouchStartNative = (e: Event) => {
      e.preventDefault();
    };

    const handleTouchMoveNative = (e: Event) => {
      if (isDragging) {
        e.preventDefault();
      }
    };

    handle.addEventListener("touchstart", handleTouchStartNative, {
      passive: false,
    });
    handle.addEventListener("touchmove", handleTouchMoveNative, {
      passive: false,
    });

    return () => {
      handle.removeEventListener("touchstart", handleTouchStartNative);
      handle.removeEventListener("touchmove", handleTouchMoveNative);
    };
  }, [isDragging]);

  // Only show on mobile (touch device) and when flooding layer group is selected
  if (!isTouchDevice || layerGroup !== LayerGroupName.Flooding) {
    return null;
  }

  return (
    <>
      <div
        className="mobile-flood-selector"
        ref={containerRef}
        style={{
          left: `${currentSlidePercent}%`,
          transform: `translateX(-50%)`,
          transition: isDragging ? "none" : "left 0.3s ease",
        }}
      >
        <div className="mobile-flood-vertical-line"></div>
        <div
          className={`mobile-flood-handle ${isDragging ? "dragging" : ""}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="mobile-flood-handle-icon">
            <div className="arrow-left">‹</div>
            <div className="arrow-right">›</div>
          </div>
        </div>
      </div>

      <div
        className="mobile-flood-selector-container"
        style={{
          left: `${currentSlidePercent}%`,
          transform: `translateX(-50%)`,
          transition: isDragging ? "none" : "left 0.3s ease",
        }}
      >
        <div className="mobile-flood-selector-buttons">
          <div className="mobile-flood-button">With Mangroves</div>
          <div className="mobile-flood-button">Without Mangroves</div>
        </div>
      </div>
    </>
  );
};
